import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import type Handsontable from 'handsontable/base';
import type { GridSettings } from 'handsontable/settings';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';
import { addClassToHotTable, removeClassFromHotTable } from '../utils/domUtils/hotTableDomMutationUtils';
import {
    fixSelectionBorderDisplay,
    repositionAutofillHandle,
} from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import useRefCallback from '../../../utils/react/useRefCallback';
import { isHotInstanceAvailable } from '../utils/hotInstanceUtils/hotTableInstanceValidationUtils';
import { TABLE_GRID_EDITOR_KEY, TABLE_TITLE_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { finishEditingElement, startEditingElement } from '../../selection/selectionActions';
import { CAPTION_EDITOR_KEY } from '../../../components/caption/captionConstants';

type HotTableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

export type UseTableSelectionHandlersReturn = {
    afterSelection: HotTableHook<'afterSelection'>;
    afterSelectionEnd: HotTableHook<'afterSelectionEnd'>;
    afterDeselect: HotTableHook<'afterDeselect'>;
};

/**
 * Custom hook for handling table cell selection operations.
 */
export const useTableSelectionHandlers = ({
    elementId,
    currentlyEditingEditorId,
    currentEditorKey,
    hotTableInstanceRef,
    hotTableContainerRef,
}: {
    elementId: string;
    currentlyEditingEditorId?: string | null;
    currentEditorKey?: string | null;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
}): UseTableSelectionHandlersReturn => {
    const dispatch = useDispatch();

    const dispatchStartEditingTableGrid = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_GRID_EDITOR_KEY,
                    editorId: getTableGridEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [dispatch, elementId],
    );

    const dispatchFinishEditingElement = useCallback(
        () => dispatch(finishEditingElement(elementId)),
        [dispatch, elementId],
    );

    const afterSelection = useRefCallback<UseTableSelectionHandlersReturn['afterSelection']>(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        requestAnimationFrame(() => {
            if (!isHotInstanceAvailable(hotInstance)) return;

            // Applied using plain javascript to improve performance, to avoid triggering another render
            addClassToHotTable(hotInstance, 'is-selecting-cell');
        });

        fixSelectionBorderDisplay(hotInstance);

        const isEditingCellOrGrid =
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId);

        if (!isEditingCellOrGrid) {
            requestAnimationFrame(() => dispatchFinishEditingElement());
        }

        repositionAutofillHandle(hotInstance, hotTableContainerRef);
    }, [currentlyEditingEditorId, dispatchFinishEditingElement, elementId, hotTableContainerRef, hotTableInstanceRef]);

    const afterSelectionEnd = useRefCallback<UseTableSelectionHandlersReturn['afterSelectionEnd']>(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        requestAnimationFrame(() => {
            if (!isHotInstanceAvailable(hotInstance)) return;

            // Applied using plain javascript to improve performance, to avoid triggering another render
            removeClassFromHotTable(hotInstance, 'is-selecting-cell');
        });

        if (currentlyEditingEditorId !== getTableGridEditorId(elementId)) {
            dispatchStartEditingTableGrid();
        }
    }, [currentlyEditingEditorId, dispatchStartEditingTableGrid, elementId, hotTableInstanceRef]);

    const afterDeselect = useRefCallback<UseTableSelectionHandlersReturn['afterDeselect']>(() => {
        // Use setTimeout here to make sure all operations have run before we check if
        // there are still selections on the table.
        setTimeout(() => {
            if (!isHotInstanceAvailable(hotTableInstanceRef.current)) return;
            if (hotTableInstanceRef.current.getSelected()) return;

            // Don't finish editing if the cell deselection occurred because of the user focusing to table title/caption
            if (currentEditorKey !== TABLE_TITLE_EDITOR_KEY && currentEditorKey !== CAPTION_EDITOR_KEY)
                dispatchFinishEditingElement();
        });
    }, [currentEditorKey, dispatchFinishEditingElement, hotTableInstanceRef]);

    return {
        afterSelection,
        afterSelectionEnd,
        afterDeselect,
    };
};

export default useTableSelectionHandlers;
